import React, { useState, useMemo } from 'react';
import Plot from 'react-plotly.js';
import { Typography } from '@mui/material';
import { ROSE_TICKS } from '../../../utils/sensorConfig';
import { buildDirectionRose } from '../../../utils/windVectorUtils';

// wind_direction_ext is retired; gridded model wind replaces it. Each source
// pairs a direction with a speed at the same height — wind_direction_grid is
// 10m, so it pairs with wind_speed_grid, NOT wind_speed_2m_grid.
//
// Module scope, not component scope: `wind` below is a useMemo dependency, and
// rebuilding this object every render would rebuild the rose every render.
const WIND_SOURCES = {
  buoy: { dir: 'wind_direction_true', spd: 'wind_speed',      label: 'Buoy' },
  grid: { dir: 'wind_direction_grid', spd: 'wind_speed_grid', label: 'Model (Grid)' },
};

const btnStyle = (selected, disabled) => ({
  padding: '4px 10px',
  borderRadius: 8,
  fontSize: 12,
  fontWeight: selected ? 600 : 400,
  border: '1px solid #e2e8f0',
  cursor: disabled ? 'default' : 'pointer',
  background: selected ? '#1e293b' : '#fff',
  color: selected ? '#fff' : '#64748b',
  opacity: disabled ? 0.4 : 1,
});

export default function DirectionRoses({ transformedData }) {
  const [windSource, setWindSource] = useState('buoy');

  const available = useMemo(
    () => Object.entries(WIND_SOURCES)
      .filter(([, s]) => transformedData[s.dir]?.length && transformedData[s.spd]?.length)
      .map(([key]) => key),
    [transformedData],
  );

  // The selected source can vanish (retired sensor, or a range with no buoy
  // wind). Fall back to whatever is present rather than rendering "No Data".
  const activeWind = available.includes(windSource) ? windSource : (available[0] ?? null);
  const wind = activeWind ? WIND_SOURCES[activeWind] : null;

  const haveWave = !!(transformedData.wave_direction?.length || transformedData.wave_direction_true?.length);
  const waveDirKind = transformedData.wave_direction?.length ? 'wave_direction' : 'wave_direction_true';

  const windRose = useMemo(
    () => (wind
      ? buildDirectionRose(transformedData, { dirKind: wind.dir, weightKind: wind.spd })
      : { theta: [], r: [] }),
    [transformedData, wind],
  );
  const waveRose = useMemo(
    () => buildDirectionRose(transformedData, { dirKind: waveDirKind }),
    [transformedData, waveDirKind],
  );

  // Buoy wind and model wind are different measurements — name the source in
  // the title so a fallback is never mistaken for the sensor.
  const roses = [
    { title: `Wind Direction${wind ? ` — ${wind.label}` : ''}`, data: windRose },
    ...(haveWave ? [{ title: 'Wave Direction', data: waveRose }] : []),
  ];

  return (
    <div style={{ background: '#fff', borderRadius: 14, padding: 24, boxShadow: '0 1px 3px rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.04)', marginBottom: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 700, color: '#1e293b', margin: 0 }}>Direction Roses</h3>
        <div style={{ display: 'flex', gap: 4 }}>
          {Object.entries(WIND_SOURCES).map(([key, src]) => {
            const disabled = !available.includes(key);
            return (
              <button
                key={key}
                onClick={() => setWindSource(key)}
                disabled={disabled}
                aria-pressed={key === activeWind}
                style={btnStyle(key === activeWind, disabled)}
              >
                {src.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
        {roses.map((rose, i) => (
          <div key={i} style={{ border: '1px solid #f1f5f9', borderRadius: 10, padding: 12 }}>
            <p style={{ textAlign: 'center', fontSize: 13, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{rose.title}</p>
            {rose.data.theta.length ? (
              <Plot
                data={[{ type: 'barpolar', theta: rose.data.theta, r: rose.data.r, marker: { line: { width: 1 } } }]}
                layout={{
                  height: 280, margin: { l: 30, r: 30, t: 10, b: 10 },
                  showlegend: false,
                  paper_bgcolor: 'rgba(0,0,0,0)', plot_bgcolor: 'rgba(0,0,0,0)',
                  polar: {
                    barmode: 'overlay',
                    angularaxis: { direction: 'clockwise', rotation: 90, tickmode: 'array', tickvals: ROSE_TICKS.vals, ticktext: ROSE_TICKS.text },
                    radialaxis: { showticklabels: false, ticks: '' },
                  },
                }}
                config={{ displayModeBar: false }}
                style={{ width: '100%' }}
              />
            ) : (
              <Typography align="center" color="textSecondary" sx={{ py: 4 }}>No Data</Typography>
            )}
          </div>
        ))}
      </div>

      <div style={{ marginTop: 12, textAlign: 'right' }}>
        <p style={{ fontSize: 12, color: '#546e7a', margin: 0 }}>
          • Rose plots use the <strong>FROM</strong> convention (meteorological standard).
        </p>
        {activeWind === 'grid' && (
          <p style={{ fontSize: 12, color: '#546e7a', margin: '4px 0 0 0' }}>
            • <strong>grid</strong> variables are sourced from gridded RTMA/HRRR/MRMS models.
          </p>
        )}
      </div>
    </div>
  );
}